/**
 * Composable for managing registration data persistence using sessionStorage
 * Handles both student and tutor registration flows with separate storage keys
 */

import { ref, watch } from 'vue';
import { logDebug, logInfo, logError } from '@/utils/secureLogger';

// Storage keys for different registration flows
const STORAGE_KEYS = {
  STUDENT_INFO: 'elevated_student_registration_info',
  STUDENT_SUBJECTS: 'elevated_student_registration_subjects',
  STUDENT_VERIFICATION: 'elevated_student_registration_verification',
  TUTOR_INFO: 'elevated_tutor_registration_info',
  TUTOR_DOCUMENTS: 'elevated_tutor_registration_documents',
  TUTOR_AGREEMENTS: 'elevated_tutor_registration_agreements',
} as const;

interface StudentInfoData {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  email: string;
  dateOfBirth: string;
  school: string;
  gradeLevel: string;
  academicTrack: string;
  // Note: password is intentionally excluded for security
}

interface StudentSubjectsData {
  selectedSubjects: string[];
}

interface StudentVerificationData {
  termsAccepted: boolean;
  studentIDFileName?: string;
  studentHoldingIdFileName?: string;
}

interface TutorInfoData {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  email: string;
  dateOfBirth: string;
  educationLevel: string;
  fieldOfStudy: string;
  institution: string;
  graduationYear: string;
  experienceYears: string;
  subjects: string[] | string; // Array of SHS subjects or comma-separated string for backwards compatibility
  // Note: password and confirmPassword are intentionally excluded for security
}

interface TutorDocumentsData {
  // Only store file names, not actual files
  validIDFileName?: string;
  proofOfEducationFileName?: string;
  certificationsFileName?: string;
}

interface TutorAgreementsData {
  studentSafety: boolean;
  liabilityTerms: boolean;
  conductStandards: boolean;
  termsAndConditions: boolean;
  legalNotice: boolean;
}

/**
 * Save data to sessionStorage with error handling
 */
function saveToStorage<T>(key: string, data: T): void {
  try {
    const serialized = JSON.stringify(data);
    sessionStorage.setItem(key, serialized);
    logDebug(`Saved data to ${key}`);
  } catch (error) {
    logError(`Failed to save data to ${key}`, error);
  }
}

/**
 * Load data from sessionStorage with error handling
 */
function loadFromStorage<T>(key: string): T | null {
  try {
    const serialized = sessionStorage.getItem(key);
    if (!serialized) {
      return null;
    }
    const data = JSON.parse(serialized) as T;
    logDebug(`Loaded data from ${key}`);
    return data;
  } catch (error) {
    logError(`Failed to load data from ${key}`, error);
    return null;
  }
}

/**
 * Remove data from sessionStorage
 */
function removeFromStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
    logDebug(`Removed data from ${key}`);
  } catch (error) {
    logError(`Failed to remove data from ${key}`, error);
  }
}

/**
 * Composable for student registration storage
 */
export function useStudentRegistrationStorage() {
  // Save student info (excluding password)
  const saveStudentInfo = (data: StudentInfoData) => {
    saveToStorage(STORAGE_KEYS.STUDENT_INFO, data);
  };

  // Load student info
  const loadStudentInfo = (): StudentInfoData | null => {
    return loadFromStorage<StudentInfoData>(STORAGE_KEYS.STUDENT_INFO);
  };

  // Save student subjects
  const saveStudentSubjects = (data: StudentSubjectsData) => {
    saveToStorage(STORAGE_KEYS.STUDENT_SUBJECTS, data);
  };

  // Load student subjects
  const loadStudentSubjects = (): StudentSubjectsData | null => {
    return loadFromStorage<StudentSubjectsData>(STORAGE_KEYS.STUDENT_SUBJECTS);
  };

  // Save student verification data
  const saveStudentVerification = (data: StudentVerificationData) => {
    saveToStorage(STORAGE_KEYS.STUDENT_VERIFICATION, data);
  };

  // Load student verification data
  const loadStudentVerification = (): StudentVerificationData | null => {
    return loadFromStorage<StudentVerificationData>(STORAGE_KEYS.STUDENT_VERIFICATION);
  };

  // Clear all student registration data
  const clearStudentRegistration = () => {
    removeFromStorage(STORAGE_KEYS.STUDENT_INFO);
    removeFromStorage(STORAGE_KEYS.STUDENT_SUBJECTS);
    removeFromStorage(STORAGE_KEYS.STUDENT_VERIFICATION);
    logInfo('Cleared all student registration data');
  };

  return {
    saveStudentInfo,
    loadStudentInfo,
    saveStudentSubjects,
    loadStudentSubjects,
    saveStudentVerification,
    loadStudentVerification,
    clearStudentRegistration,
  };
}

/**
 * Composable for tutor registration storage
 */
export function useTutorRegistrationStorage() {
  // Save tutor info (excluding passwords)
  const saveTutorInfo = (data: TutorInfoData) => {
    saveToStorage(STORAGE_KEYS.TUTOR_INFO, data);
  };

  // Load tutor info
  const loadTutorInfo = (): TutorInfoData | null => {
    return loadFromStorage<TutorInfoData>(STORAGE_KEYS.TUTOR_INFO);
  };

  // Save tutor documents metadata
  const saveTutorDocuments = (data: TutorDocumentsData) => {
    saveToStorage(STORAGE_KEYS.TUTOR_DOCUMENTS, data);
  };

  // Load tutor documents metadata
  const loadTutorDocuments = (): TutorDocumentsData | null => {
    return loadFromStorage<TutorDocumentsData>(STORAGE_KEYS.TUTOR_DOCUMENTS);
  };

  // Save tutor agreements
  const saveTutorAgreements = (data: TutorAgreementsData) => {
    saveToStorage(STORAGE_KEYS.TUTOR_AGREEMENTS, data);
  };

  // Load tutor agreements
  const loadTutorAgreements = (): TutorAgreementsData | null => {
    return loadFromStorage<TutorAgreementsData>(STORAGE_KEYS.TUTOR_AGREEMENTS);
  };

  // Clear all tutor registration data
  const clearTutorRegistration = () => {
    removeFromStorage(STORAGE_KEYS.TUTOR_INFO);
    removeFromStorage(STORAGE_KEYS.TUTOR_DOCUMENTS);
    removeFromStorage(STORAGE_KEYS.TUTOR_AGREEMENTS);
    logInfo('Cleared all tutor registration data');
  };

  return {
    saveTutorInfo,
    loadTutorInfo,
    saveTutorDocuments,
    loadTutorDocuments,
    saveTutorAgreements,
    loadTutorAgreements,
    clearTutorRegistration,
  };
}

// Export types for use in components
export type {
  StudentInfoData,
  StudentSubjectsData,
  StudentVerificationData,
  TutorInfoData,
  TutorDocumentsData,
  TutorAgreementsData,
};

